import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, Platform } from 'react-native';
import { TextInput, Button, Title, Text, Snackbar, HelperText, useTheme, 
  Switch, List, Checkbox, Avatar } from 'react-native-paper';
import { createActivity, getActivityById, updateActivity, getMyChildren } from '../../api';
import { isValidPrice } from '../../utils';
import DateTimePicker from '@react-native-community/datetimepicker';

const CreateActivityScreen = ({ navigation, route }) => {
  const theme = useTheme();
  const activityId = route.params?.activityId;
  const selectedChildId = route.params?.selectedChildId;
  const isEditing = !!activityId;
  
  // State variables
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    date: new Date(),
    startTime: new Date(),
    endTime: new Date(new Date().getTime() + 60 * 60 * 1000), // 1 saat sonrası
    childId: selectedChildId || '',
    price: '',
    location: '',
  });
  
  // UI state variables
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [hasDuration, setHasDuration] = useState(true);
  const [childrenList, setChildrenList] = useState([]);
  const [showChildrenSelection, setShowChildrenSelection] = useState(false);
  
  // Fetch necessary data
  useEffect(() => {
    fetchChildren();
    
    if (isEditing) {
      fetchActivityData();
    }
  }, [activityId]);
  
  // Fetch children data
  const fetchChildren = async () => {
    try {
      const response = await getMyChildren();
      setChildrenList(response.children || []);
    } catch (err) {
      console.error('Error fetching children:', err);
      setSnackbarMessage('Çocuk bilgileri yüklenirken bir hata oluştu');
      setSnackbarVisible(true);
    }
  };
  
  // Fetch activity data if editing
  const fetchActivityData = async () => {
    setInitialLoading(true);
    
    try {
      const response = await getActivityById(activityId);
      const activity = response.activity;
      
      if (activity) {
        const hasStartEnd = activity.startTime && activity.endTime;
        
        setHasDuration(hasStartEnd);
        
        setFormData({
          name: activity.name || '',
          description: activity.description || '',
          date: activity.date ? new Date(activity.date) : new Date(),
          startTime: activity.startTime ? new Date(activity.startTime) : new Date(),
          endTime: activity.endTime ? new Date(activity.endTime) : new Date(new Date().getTime() + 60 * 60 * 1000),
          childId: activity.child?.id || '',
          price: activity.price ? activity.price.toString() : '',
          location: activity.location || '',
        });
      }
    } catch (err) {
      console.error('Error fetching activity data:', err);
      setSnackbarMessage(err.message || 'Aktivite bilgileri yüklenirken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setInitialLoading(false);
    }
  };
  
  // Input change handler
  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user types
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };
  
  // Date change handler
  const handleDateChange = (event, selectedDate) => {
    setShowDatePicker(false);
    if (selectedDate) {
      // Sadece tarihi değiştir, saati değil
      const newDate = new Date(selectedDate);
      handleChange('date', newDate);
    }
  };
  
  // Start time change handler
  const handleStartTimeChange = (event, selectedTime) => {
    setShowStartTimePicker(false);
    if (selectedTime) {
      // Tarihi koruyup saati güncelle
      const newStartTime = new Date(selectedTime);
      handleChange('startTime', newStartTime);
      
      // Eğer bitiş saati başlangıç saatinden önce ise, bitiş saatini güncelle
      if (formData.endTime < newStartTime) {
        const newEndTime = new Date(newStartTime.getTime() + 60 * 60 * 1000); // 1 saat sonrası
        handleChange('endTime', newEndTime);
      }
    }
  };
  
  // End time change handler
  const handleEndTimeChange = (event, selectedTime) => {
    setShowEndTimePicker(false);
    if (selectedTime) {
      const newEndTime = new Date(selectedTime);
      
      // Bitiş saati başlangıç saatinden önce ise hata göster
      if (newEndTime < formData.startTime) {
        setErrors({
          ...errors,
          endTime: 'Bitiş saati başlangıç saatinden önce olamaz'
        });
      } else {
        handleChange('endTime', newEndTime);
      }
    }
  };
  
  // Toggle children selection
  const toggleChildrenSelection = () => {
    setShowChildrenSelection(!showChildrenSelection);
  };
  
  // Select child
  const handleSelectChild = (childId) => {
    handleChange('childId', childId);
    setShowChildrenSelection(false);
  };
  
  // Toggle duration switch
  const toggleDuration = () => {
    setHasDuration(!hasDuration);
    if (!hasDuration) {
      // Varsayılan değerler
      const startTime = new Date();
      const endTime = new Date(startTime.getTime() + 60 * 60 * 1000); // 1 saat sonrası
      
      handleChange('startTime', startTime);
      handleChange('endTime', endTime);
    }
  };
  
  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name) {
      newErrors.name = 'Aktivite adı gereklidir';
    }
    
    // Date validation
    if (!formData.date) {
      newErrors.date = 'Tarih gereklidir';
    }
    
    // Start time and end time validation if duration is enabled
    if (hasDuration) {
      if (!formData.startTime) {
        newErrors.startTime = 'Başlangıç saati gereklidir';
      }
      
      if (!formData.endTime) {
        newErrors.endTime = 'Bitiş saati gereklidir';
      } else if (formData.endTime <= formData.startTime) {
        newErrors.endTime = 'Bitiş saati başlangıç saatinden sonra olmalıdır';
      }
    }
    
    // Child validation
    if (!formData.childId) {
      newErrors.childId = 'Çocuk seçimi gereklidir';
    }
    
    // Price validation
    if (formData.price && !isValidPrice(formData.price)) {
      newErrors.price = 'Geçerli bir fiyat giriniz';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Format date for display
  const formatDate = (date) => {
    if (!date) return '';
    
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}.${month}.${year}`;
  };
  
  // Format time for display
  const formatTime = (date) => {
    if (!date) return '';
    
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    
    return `${hours}:${minutes}`;
  };
  
  // Find selected child
  const getSelectedChild = () => {
    return childrenList.find(child => child.id === formData.childId);
  };
  
  // Save activity
  const handleSave = async () => {
    // Form validation
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    // Prepare data for API
    const activityData = {
      ...formData,
      // Parse price to number
      price: formData.price ? parseFloat(formData.price) : undefined,
      // Remove start/end times if duration is disabled
      startTime: hasDuration ? formData.startTime : undefined,
      endTime: hasDuration ? formData.endTime : undefined,
    };
    
    try {
      if (isEditing) {
        // Update existing activity
        await updateActivity(activityId, activityData);
        setSnackbarMessage('Aktivite başarıyla güncellendi');
      } else {
        // Create new activity
        await createActivity(activityData);
        setSnackbarMessage('Aktivite başarıyla oluşturuldu');
        
        // Reset form after successful creation
        setFormData({
          name: '',
          description: '',
          date: new Date(),
          startTime: new Date(),
          endTime: new Date(new Date().getTime() + 60 * 60 * 1000),
          childId: '',
          price: '',
          location: '',
        });
      }
      
      setSnackbarVisible(true);
      
      // Navigate back after a delay
      setTimeout(() => {
        navigation.goBack();
      }, 1500);
    } catch (err) {
      console.error('Error saving activity:', err);
      setSnackbarMessage(err.message || 'Aktivite kaydedilirken bir hata oluştu');
      setSnackbarVisible(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Loading state
  if (initialLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Aktivite bilgileri yükleniyor...</Text>
      </View>
    );
  }
  
  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={styles.container}
    >
      <ScrollView 
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.headerContainer}>
          <Title style={styles.title}>
            {isEditing ? 'Aktiviteyi Düzenle' : 'Yeni Aktivite Oluştur'}
          </Title>
          <Text style={styles.subtitle}>
            {isEditing 
              ? 'Aktivite bilgilerini güncelleyin' 
              : 'Çocuğunuz için yeni bir aktivite oluşturun'}
          </Text>
        </View>
        
        <View style={styles.formContainer}>
          {/* Temel Bilgiler */}
          <TextInput
            label="Aktivite Adı *"
            value={formData.name}
            onChangeText={(value) => handleChange('name', value)}
            mode="outlined"
            error={!!errors.name}
            style={styles.input}
            left={<TextInput.Icon icon="format-title" />}
            disabled={isLoading}
          />
          {errors.name ? <HelperText type="error">{errors.name}</HelperText> : null}
          
          <TextInput
            label="Açıklama"
            value={formData.description}
            onChangeText={(value) => handleChange('description', value)}
            mode="outlined"
            style={[styles.input, styles.multilineInput]}
            left={<TextInput.Icon icon="text-box" />}
            disabled={isLoading}
            multiline
            numberOfLines={3}
          />
          
          {/* Tarih ve Saat */}
          <TextInput
            label="Tarih *"
            value={formatDate(formData.date)}
            onFocus={() => setShowDatePicker(true)}
            mode="outlined"
            error={!!errors.date}
            style={styles.input}
            left={<TextInput.Icon icon="calendar" />}
            right={<TextInput.Icon icon="calendar-edit" onPress={() => setShowDatePicker(true)} />}
            showSoftInputOnFocus={false}
            disabled={isLoading}
          />
          {errors.date ? <HelperText type="error">{errors.date}</HelperText> : null}
          
          {showDatePicker && (
            <DateTimePicker
              value={formData.date || new Date()}
              mode="date"
              display="default"
              onChange={handleDateChange}
            />
          )}
          
          {/* Süre Switch */}
          <View style={styles.switchContainer}>
            <Text>Başlangıç ve Bitiş Saati Belirle</Text>
            <Switch
              value={hasDuration}
              onValueChange={toggleDuration}
              color={theme.colors.primary}
              disabled={isLoading}
            />
          </View>
          
          {hasDuration && (
            <View>
              <TextInput
                label="Başlangıç Saati *"
                value={formatTime(formData.startTime)}
                onFocus={() => setShowStartTimePicker(true)}
                mode="outlined"
                error={!!errors.startTime}
                style={styles.input}
                left={<TextInput.Icon icon="clock-start" />}
                right={<TextInput.Icon icon="clock-edit" onPress={() => setShowStartTimePicker(true)} />}
                showSoftInputOnFocus={false}
                disabled={isLoading}
              />
              {errors.startTime ? <HelperText type="error">{errors.startTime}</HelperText> : null}
              
              {showStartTimePicker && (
                <DateTimePicker
                  value={formData.startTime || new Date()}
                  mode="time"
                  display="default"
                  onChange={handleStartTimeChange}
                />
              )}
              
              <TextInput
                label="Bitiş Saati *"
                value={formatTime(formData.endTime)}
                onFocus={() => setShowEndTimePicker(true)}
                mode="outlined"
                error={!!errors.endTime}
                style={styles.input}
                left={<TextInput.Icon icon="clock-end" />}
                right={<TextInput.Icon icon="clock-edit" onPress={() => setShowEndTimePicker(true)} />}
                showSoftInputOnFocus={false}
                disabled={isLoading}
              />
              {errors.endTime ? <HelperText type="error">{errors.endTime}</HelperText> : null}
              
              {showEndTimePicker && (
                <DateTimePicker
                  value={formData.endTime || new Date()}
                  mode="time"
                  display="default"
                  onChange={handleEndTimeChange}
                />
              )}
            </View>
          )}
          
          {/* Çocuk Seçimi */}
          <List.Accordion
            title={formData.childId ? getSelectedChild()?.name || 'Çocuk Seçin *' : 'Çocuk Seçin *'}
            description={formData.childId ? getSelectedChild()?.surname || '' : 'Aktivitenin hangi çocuk için olduğunu seçin'}
            left={props => <List.Icon {...props} icon="account-child" />}
            expanded={showChildrenSelection}
            onPress={toggleChildrenSelection}
            style={[styles.childSelector, errors.childId && { borderColor: theme.colors.error }]}
          >
            {childrenList.length === 0 ? (
              <List.Item
                title="Henüz Çocuk Eklenmemiş"
                description="Önce çocuk profili eklemelisiniz"
                left={props => <List.Icon {...props} icon="account-alert" />}
                onPress={() => navigation.navigate('Children', { screen: 'AddChild' })}
              />
            ) : (
              childrenList.map(child => (
                <List.Item
                  key={child.id}
                  title={`${child.name} ${child.surname}`}
                  description={`${child.age || ''} ${child.gender === 'male' ? 'Erkek' : 'Kız'}`}
                  left={props => 
                    <Avatar.Icon 
                      {...props} 
                      icon={child.gender === 'male' ? 'gender-male' : 'gender-female'} 
                      size={36}
                      style={{ backgroundColor: child.gender === 'male' ? '#3498db' : '#e84393' }}
                    />
                  }
                  right={props => 
                    <Checkbox
                      status={formData.childId === child.id ? 'checked' : 'unchecked'}
                      onPress={() => handleSelectChild(child.id)}
                    />
                  }
                  onPress={() => handleSelectChild(child.id)}
                />
              ))
            )}
          </List.Accordion>
          {errors.childId ? <HelperText type="error">{errors.childId}</HelperText> : null}
          
          {/* Ek Bilgiler */}
          <TextInput
            label="Fiyat (TL)"
            value={formData.price}
            onChangeText={(value) => handleChange('price', value)}
            mode="outlined"
            keyboardType="numeric"
            error={!!errors.price}
            style={styles.input}
            left={<TextInput.Icon icon="currency-try" />}
            disabled={isLoading}
          />
          {errors.price ? <HelperText type="error">{errors.price}</HelperText> : null}
          
          <TextInput
            label="Konum"
            value={formData.location}
            onChangeText={(value) => handleChange('location', value)}
            mode="outlined"
            style={styles.input}
            left={<TextInput.Icon icon="map-marker" />}
            disabled={isLoading}
          />
          
          {/* Kaydet Butonu */}
          <Button
            mode="contained"
            onPress={handleSave}
            style={styles.button}
            loading={isLoading}
            disabled={isLoading}
          >
            {isEditing ? 'Güncelle' : 'Kaydet'}
          </Button>
        </View>
      </ScrollView>
      
      <Snackbar
        visible={snackbarVisible}
        onDismiss={() => setSnackbarVisible(false)}
        duration={3000}
        action={{
          label: 'Tamam',
          onPress: () => setSnackbarVisible(false),
        }}
      >
        {snackbarMessage}
      </Snackbar>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9f9f9',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
  },
  scrollContent: {
    flexGrow: 1,
    padding: 20,
  },
  headerContainer: {
    alignItems: 'center',
    marginVertical: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#3498db',
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
    marginTop: 4,
  },
  formContainer: {
    width: '100%',
  },
  input: {
    marginBottom: 8,
  },
  multilineInput: {
    height: 80,
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginVertical: 12,
    paddingHorizontal: 8,
  },
  childSelector: {
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    marginVertical: 8,
  },
  button: {
    marginTop: 24,
    height: 50,
    justifyContent: 'center',
    backgroundColor: '#3498db',
    marginBottom: 20,
  },
});

export default CreateActivityScreen;
